import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatIST } from '@/lib/utils';
import api from '@/lib/api';
import {
  AlertTriangle, ArrowDownRight, ArrowUpRight, BarChart3, Minus, PhoneCall, RefreshCw,
} from 'lucide-react';

import { Screen, ScreenSplit, Pane, FactBand, Fact, CommandBar, DataGrid } from '@/components/data';
import { useIsMobile } from '@/hooks/useIsMobile';
import PhoneDashboard from './Dashboard/PhoneDashboard';
import { inr, exact, plural } from './Dashboard/format';

/**
 * The firm through its customers' eyes.
 *
 * What was here before was a shift dashboard — orders this week, active count,
 * a stage chart, an audit feed — which is the floor's set of questions. This
 * one asks the owner's four, in the order that grows the business: are we
 * keeping the dates we promise, who are we letting down right now, is the work
 * coming out right, and are customers coming back. The book is on the screen
 * as one band of context, because an owner who fixes those four does not have
 * to look at it and one who only looks at it cannot fix them.
 *
 * The heart of it is "Who to ring today" — every customer with something wrong,
 * once, with the reasons beside them. A dashboard that reports is a dashboard
 * nobody opens twice; this one hands over a list of phone calls.
 *
 * Two rules the page keeps:
 *   - a number never appears alone. Each headline carries the line that says
 *     whether it is good — the direction it moved, who is behind the count,
 *     how old the oldest one is.
 *   - a rate is withheld rather than guessed. Below the server's minimum the
 *     page says there is not enough finished work to judge, because "0% on
 *     time" off one late job is not a fact about the firm.
 *
 * It is one request (`/dashboard/owner`), so the screen draws whole or not at
 * all rather than filling in over six round trips.
 *
 * The sentences under the numbers are built here as data and handed to both
 * layouts, so the phone the owner opens over breakfast says exactly what the
 * laptop says — a note worded one way on one screen and another way on the
 * other is two dashboards.
 */

const greeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const TREND_ICON = { up: ArrowUpRight, down: ArrowDownRight, flat: Minus };

// the tones a headline or fact can carry, named once so the phone can resolve
// the same names to its own palette
const TEXT_TONE = {
  positive: 'text-emerald-600',
  caution: 'text-amber-600',
  critical: 'text-red-600',
  muted: 'text-slate-400',
  neutral: 'text-slate-500',
};

const SEVERITY_TONE = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-slate-100 text-slate-600',
};

/**
 * A headline number with the sentence that makes it mean something.
 *
 * Deliberately taller and plainer than the StatStrip the work screens use: this
 * one is read across the room a couple of times a day, not scanned while typing.
 */
function Headline({ label, value, valueTone, note, noteTone, trendDir, onClick, testId }) {
  const Icon = trendDir && TREND_ICON[trendDir];
  const valueClass = TEXT_TONE[valueTone] || 'text-slate-900';
  const noteClass = TEXT_TONE[noteTone] || 'text-slate-500';
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      data-testid={testId}
      className={`flex min-w-0 flex-col justify-between gap-1 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-left shadow-card ${
        onClick ? 'transition-shadow hover:shadow-card-hover' : ''
      }`}
    >
      <span className="truncate text-[11px] font-medium uppercase tracking-wider text-slate-500">{label}</span>
      <span className={`truncate font-heading text-2xl font-bold tabular-nums ${valueClass}`}>
        {value}
      </span>
      <span className={`flex items-center gap-1 truncate text-[11px] ${noteClass}`}>
        {Icon && <Icon className="h-3 w-3 shrink-0" />}
        <span className="truncate">{note || ' '}</span>
      </span>
    </Tag>
  );
}

/** A ranked row with the bar drawn behind it, so the shape of the list reads before the numbers do. */
function BarRow({ label, sub, value, aside, share, tone = 'bg-brand-blue/10' }) {
  return (
    <div className="relative flex items-center gap-2 border-b border-slate-100 px-2.5 py-1.5 last:border-b-0">
      <span className={`absolute inset-y-0 left-0 ${tone}`} style={{ width: `${Math.max(share, 2)}%` }} aria-hidden />
      <span className="relative min-w-0 flex-1">
        <span className="block truncate text-[12px] font-medium text-slate-800">{label}</span>
        {sub && <span className="block truncate text-[10px] text-slate-500">{sub}</span>}
      </span>
      {aside && <span className="relative shrink-0 text-[10px] text-slate-500">{aside}</span>}
      <span className="relative shrink-0 text-[12px] font-semibold tabular-nums text-slate-900">{value}</span>
    </div>
  );
}

/** One reason a customer is on the call list, worded the way it would be said out loud. */
function Reason({ tone, children }) {
  return (
    <span className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium ${tone}`}>
      {children}
    </span>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [tab, setTab] = useState('calls');

  const load = async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get('/dashboard/owner');
      setData(payload);
      setFailed(false);
    } catch {
      setFailed(true);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const promise = data?.promise || {};
  const quality = data?.quality || {};
  const loyalty = data?.loyalty || {};
  const work = data?.work || {};
  const money = data?.money || {};
  const attention = useMemo(() => data?.attention || [], [data]);
  const late = useMemo(() => data?.late || [], [data]);
  const dueSoon = useMemo(() => data?.due_soon || [], [data]);
  const complaints = data?.complaints || [];
  const stages = data?.stages || [];

  const callColumns = useMemo(() => ([
    {
      key: 'customer',
      header: 'Customer',
      render: (c) => (
        <>
          <span className="block truncate font-semibold text-slate-900">{c.firm_name || c.customer_name}</span>
          {c.firm_name && c.customer_name !== c.firm_name && (
            <span className="block truncate text-[11px] leading-tight text-slate-500">{c.customer_name}</span>
          )}
        </>
      ),
    },
    {
      key: 'why',
      header: 'Why',
      width: '22rem',
      render: (c) => (
        <span className="flex flex-wrap items-center gap-1">
          {c.late_orders > 0 && (
            <Reason tone="bg-red-100 text-red-700">
              {plural(c.late_orders, 'order')} late by up to {plural(c.worst_days_late, 'day')}
            </Reason>
          )}
          {c.open_complaints > 0 && (
            <Reason tone={SEVERITY_TONE[c.worst_severity] || SEVERITY_TONE.medium}>
              {plural(c.open_complaints, 'open complaint')}
            </Reason>
          )}
          {c.owed_stale > 0 && (
            <Reason tone="bg-amber-100 text-amber-700">
              {inr(c.owed_stale)} unpaid {c.owed_days} days
            </Reason>
          )}
          {c.quiet_days > 0 && (
            <Reason tone="bg-slate-100 text-slate-600">
              no order in {c.quiet_days} days
            </Reason>
          )}
        </span>
      ),
    },
  ]), []);

  const riskColumns = useMemo(() => ([
    {
      key: 'customer',
      header: 'Customer',
      render: (o) => (
        <>
          <span className="block truncate font-semibold text-slate-900">{o.firm_name || o.customer_name}</span>
          <span className="block truncate text-[11px] leading-tight text-slate-500">
            {o.item_name || o.order_id}
          </span>
        </>
      ),
    },
    {
      key: 'days',
      header: tab === 'late' ? 'Late by' : 'Due in',
      width: '6.5rem',
      render: (o) => {
        const days = tab === 'late' ? o.days_late : -o.days_late;
        const tone = tab === 'late'
          ? (days >= 7 ? 'text-red-600' : 'text-amber-600')
          : 'text-slate-700';
        return (
          <span className={`whitespace-nowrap text-[12px] font-semibold ${tone}`}>
            {days <= 0 ? 'today' : plural(days, 'day')}
          </span>
        );
      },
    },
    {
      key: 'due',
      header: 'Promised',
      width: '6.5rem',
      mobile: false,
      render: (o) => (
        <span className="whitespace-nowrap text-[11px] text-slate-500">
          {o.expected_delivery_date ? formatIST(o.expected_delivery_date, { dateOnly: true }) : '—'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Stage',
      width: '8rem',
      mobile: false,
      render: (o) => (
        <span className="truncate text-[11px] capitalize text-slate-500">
          {(o.status || '').replace(/_/g, ' ')}
        </span>
      ),
    },
    {
      key: 'value',
      header: 'Value',
      width: '6rem',
      align: 'right',
      render: (o) => (
        <span className="whitespace-nowrap text-[12px] tabular-nums text-slate-800" title={exact(o.order_value)}>
          {o.order_value ? inr(o.order_value) : '—'}
        </span>
      ),
    },
  ]), [tab]);

  // the direction only means something once there is a quarter behind it to
  // compare against; a first quarter has no trend, only a number
  const kept = promise.percentage;
  const before = promise.previous_percentage;
  const shift = kept != null && before != null ? kept - before : null;
  const trend = shift == null ? undefined : shift > 0 ? 'up' : shift < 0 ? 'down' : 'flat';
  // the server withholds every figure drawn off the window when too few orders
  // finished in it; the band has to say why it is blank rather than read the
  // absent overrun as "we never missed one"
  const thinSample = kept == null;
  const promiseNote = kept == null
    ? `only ${plural(promise.measured || 0, 'order')} delivered — too few to judge`
    : shift == null ? `${promise.measured} orders, last ${promise.window_days} days`
      : shift === 0 ? 'holding steady on last quarter'
        : `${Math.abs(shift)} points ${shift > 0 ? 'better' : 'worse'} than last quarter`;

  const stalePct = money.owed ? Math.round((money.owed_stale / money.owed) * 100) : 0;
  const busiest = stages[0]?.waiting || 1;

  const headlines = [
    {
      key: 'promise',
      label: 'Delivered on time',
      value: kept == null ? '—' : `${kept}%`,
      valueTone: kept == null ? 'muted' : kept >= 85 ? 'positive' : 'caution',
      note: promiseNote,
      noteTone: { up: 'positive', down: 'critical', flat: 'neutral' }[trend] || 'neutral',
      trendDir: trend,
    },
    {
      key: 'late',
      label: 'Customers waiting',
      value: work.customers_let_down || 0,
      valueTone: work.customers_let_down ? 'critical' : 'positive',
      note: late.length
        ? `${plural(late.length, 'order')} past its promised date`
        : 'nobody is waiting past their date',
      noteTone: late.length ? 'critical' : 'positive',
      onClick: () => setTab('late'),
    },
    {
      key: 'quality',
      label: 'Open complaints',
      value: quality.open || 0,
      valueTone: quality.open ? 'critical' : 'positive',
      note: quality.open
        ? `oldest ${plural(quality.oldest_days || 0, 'day')}${quality.serious ? ` · ${quality.serious} serious` : ''}`
        : 'nothing outstanding',
      noteTone: quality.open ? 'caution' : 'positive',
    },
    {
      key: 'loyalty',
      label: 'Repeat business',
      value: loyalty.returning_share == null ? '—' : `${loyalty.returning_share}%`,
      valueTone: loyalty.returning_share == null ? 'muted' : undefined,
      note: loyalty.month_orders
        ? `${loyalty.returning_orders} of ${plural(loyalty.month_orders, 'order')} this month from returning customers`
        : 'no orders booked this month yet',
    },
  ];

  const facts = [
    {
      key: 'turnaround',
      label: 'Turnaround',
      title: 'Average days from booking to delivery, over the last quarter',
      value: thinSample ? 'not enough delivered yet' : `${promise.avg_turnaround_days} days`,
      valueTone: thinSample ? 'muted' : undefined,
      sub: thinSample ? null : 'order to door',
    },
    {
      key: 'overrun',
      label: 'When late',
      title: 'When we do miss a date, by how much — a one-day slip and a three-week slip are not the same failure',
      value: thinSample
        ? 'not enough delivered yet'
        : promise.avg_days_late == null ? 'no misses in the quarter' : `${promise.avg_days_late} days`,
      valueTone: thinSample ? 'muted' : promise.avg_days_late == null ? 'positive' : undefined,
      sub: thinSample || promise.avg_days_late == null ? null : 'average overrun',
    },
    {
      key: 'new',
      label: 'New customers',
      title: 'Customers whose first order with us was this month',
      value: loyalty.new_customers,
      sub: loyalty.quiet_customers > 0 ? `${loyalty.quiet_customers} gone quiet` : null,
      subTone: 'caution',
    },
    {
      key: 'orders',
      label: 'Orders',
      title: 'Booked this month, and today',
      value: work.orders_this_month,
      sub: `${work.orders_today} today`,
    },
    {
      key: 'in-hand',
      label: 'In hand',
      title: 'Open orders — in design, in production, or waiting to go out',
      value: work.in_hand,
      sub: work.awaiting_dispatch > 0 ? `${work.awaiting_dispatch} ready to go` : null,
      subTone: 'caution',
    },
    {
      key: 'billed',
      label: 'Billed',
      title: exact(money.billed_this_month),
      value: inr(money.billed_this_month),
      sub: 'this month',
    },
    {
      key: 'owed',
      label: 'Owed',
      title: exact(money.owed),
      value: inr(money.owed),
      sub: money.owed_stale > 0 ? `${stalePct}% over ${money.owed_stale_after_days} days` : null,
      subTone: 'caution',
    },
  ];

  const title = `${greeting()}, ${user?.full_name || ''}`;
  const today = formatIST(new Date().toISOString(), { dateOnly: true });

  if (isMobile) {
    return (
      <PhoneDashboard
        title={title} today={today}
        loading={loading} failed={failed} onRefresh={load}
        onReports={() => navigate('/reports')}
        headlines={headlines} facts={facts}
        tab={tab} onTab={setTab}
        attention={attention} late={late} dueSoon={dueSoon}
        complaints={complaints} stages={stages} busiest={busiest}
        onCustomer={(c) => navigate(`/customers/${c.customer_id}`)}
        onOrder={(id) => navigate(`/orders/${id}`)}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-blue" />
      </div>
    );
  }

  if (failed) {
    return (
      <Screen data-testid="dashboard-page">
        <CommandBar
          title="Dashboard"
          actions={[{ key: 'refresh', label: 'Try again', icon: RefreshCw, onClick: load, tone: 'primary' }]}
        />
        <Pane title="Could not load">
          <p className="p-6 text-center text-sm text-slate-500">
            The dashboard did not answer. Nothing is wrong with your data — try again.
          </p>
        </Pane>
      </Screen>
    );
  }

  return (
    <Screen data-testid="dashboard-page">
      <CommandBar
        title={title}
        subtitle={today}
        actions={[
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: load },
          { key: 'reports', label: 'Reports', icon: BarChart3, onClick: () => navigate('/reports') },
        ]}
      />

      <div className="grid shrink-0 grid-cols-2 gap-2.5 xl:grid-cols-4" data-testid="metrics-grid">
        {headlines.map(({ key, ...h }) => <Headline key={key} testId={`headline-${key}`} {...h} />)}
      </div>

      <FactBand min="12rem" className="shrink-0">
        {facts.map((f) => (
          <Fact key={f.key} label={f.label} title={f.title}>
            {f.valueTone ? <span className={TEXT_TONE[f.valueTone]}>{f.value}</span> : f.value}
            {f.sub != null && (
              <span className={`text-[11px] ${TEXT_TONE[f.subTone] || 'text-slate-400'}`}>{f.sub}</span>
            )}
          </Fact>
        ))}
      </FactBand>

      <ScreenSplit
        railWidth="21rem"
        rail={
          <>
            <Pane
              title="Open complaints"
              aside={complaints.length ? `${complaints.length} unresolved` : undefined}
              className="shrink-0"
              scroll={false}
            >
              {complaints.length === 0 ? (
                <p className="p-4 text-center text-xs text-slate-400">
                  Nothing outstanding — every issue raised has been closed out.
                </p>
              ) : complaints.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => navigate(`/orders/${c.order_uuid}`)}
                  className="flex w-full items-start gap-2 border-b border-slate-100 px-2.5 py-1.5 text-left last:border-b-0 hover:bg-slate-50"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12px] font-medium text-slate-800">
                      {c.firm_name || c.customer_name}
                    </span>
                    <span className="block truncate text-[10px] text-slate-500">
                      {c.description || c.item_name || c.order_id}
                    </span>
                  </span>
                  <span className="flex shrink-0 flex-col items-end gap-0.5">
                    <span className={`rounded px-1 text-[10px] font-medium ${SEVERITY_TONE[c.severity] || SEVERITY_TONE.medium}`}>
                      {c.severity}
                    </span>
                    <span className="text-[10px] text-slate-400">{plural(c.days_open, 'day')}</span>
                  </span>
                </button>
              ))}
            </Pane>

            <Pane
              title="Where the work is"
              aside="queued now · days to get through"
              className="min-h-0 flex-1"
            >
              {stages.length === 0 ? (
                <p className="p-4 text-center text-xs text-slate-400">Nothing on the floor right now.</p>
              ) : stages.map((s) => (
                <BarRow
                  key={s.stage}
                  label={s.stage}
                  sub={[
                    s.avg_days == null ? null : `${s.avg_days}d to clear`,
                    s.reject_rate ? `${s.reject_rate}% spoiled` : null,
                  ].filter(Boolean).join(' · ')}
                  value={s.waiting}
                  share={(s.waiting / busiest) * 100}
                  tone={s.reject_rate >= 5 ? 'bg-red-50' : 'bg-brand-orange/12'}
                />
              ))}
            </Pane>
          </>
        }
      >
        <Pane
          title={tab === 'calls' ? 'Who to ring today' : tab === 'late' ? 'Running late' : 'Due in the next 3 days'}
          aside={
            tab === 'calls'
              ? `${plural(attention.length, 'customer')} with something wrong`
              : plural((tab === 'late' ? late : dueSoon).length, 'order')
          }
          scroll={false}
          bodyClassName="flex flex-col"
          actions={
            <span className="flex items-center gap-0.5 rounded bg-white/10 p-0.5">
              {[
                { key: 'calls', label: `Calls ${attention.length}` },
                { key: 'late', label: `Late ${late.length}` },
                { key: 'soon', label: `Due soon ${dueSoon.length}` },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  aria-pressed={tab === t.key}
                  data-testid={`board-tab-${t.key}`}
                  className={`rounded px-2 py-0.5 text-[11px] font-medium transition-colors ${
                    tab === t.key ? 'bg-white text-brand-blue' : 'text-white/80 hover:text-white'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </span>
          }
        >
          {tab === 'calls' ? (
            <DataGrid
              data-testid="call-list"
              columns={callColumns}
              rows={attention}
              getRowId={(c) => c.customer_id}
              onRowClick={(c) => navigate(`/customers/${c.customer_id}`)}
              empty={
                <span className="flex flex-col items-center gap-2 text-slate-400">
                  <PhoneCall className="h-8 w-8 text-emerald-300" />
                  Nobody to chase — no missed dates, no open complaints, no stale bills.
                </span>
              }
            />
          ) : (
            <DataGrid
              data-testid="at-risk-table"
              columns={riskColumns}
              rows={tab === 'late' ? late : dueSoon}
              getRowId={(o) => o.id}
              onRowClick={(o) => navigate(`/orders/${o.id}`)}
              empty={
                <span className="flex flex-col items-center gap-2 text-slate-400">
                  <AlertTriangle className="h-8 w-8 text-emerald-300" />
                  {tab === 'late'
                    ? 'Nothing is late — every open order is inside its promised date.'
                    : 'Nothing falls due in the next three days.'}
                </span>
              }
            />
          )}
        </Pane>
      </ScreenSplit>
    </Screen>
  );
}
